import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  quarantineResponses,
  readResponseQuality,
  readResponseQualityCounts,
} from '@api/survey_dashboard';
import { Error, toast } from '@utilities';

import OverviewMetrics from './components/OverviewMetrics';
import ResponseTable, { RATER_FILTER_IDS } from './components/ResponseTable';

import styles from './_index.module.scss';

const PAGE_SIZE = 25;

const ResponseQuality = ({ publicToken, surveyId, surveyQuestions }) => {
  const [activeFilters, setActiveFilters] = useState({});
  const [counts, setCounts] = useState({ excluded: null, included: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [isCountsLoading, setIsCountsLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [responses, setResponses] = useState([]);
  const [searchId, setSearchId] = useState('');
  const [sortBy, setSortBy] = useState([{ desc: false, id: 'id' }]);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    fetchCounts(controller.signal);
    return () => controller.abort();
  }, [surveyId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchResponses(controller.signal);
    return () => controller.abort();
  }, [surveyId, activeFilters, currentPage, searchId, sortBy]);

  const fetchCounts = async (signal) => {
    setIsCountsLoading(true);
    try {
      const result = await readResponseQualityCounts({ signal, surveyId });
      setCounts(result);
    } catch (err) {
      if (!signal.aborted) {
        console.warn('[ResponseQuality] Failed to fetch counts', err);
      }
    }
    if (!signal.aborted) {
      setIsCountsLoading(false);
    }
  };

  const fetchResponses = async (signal) => {
    setIsLoading(true);
    setError(null);

    const params = {
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
      ordering: `${sortBy[0]?.desc ? '-' : ''}${sortBy[0]?.id || 'id'}`,
      ...(publicToken ? { publicToken } : {}),
    };

    if (activeFilters.excluded) {
      params.review_status = 3;
    }

    const activeRaterFilters = RATER_FILTER_IDS.filter((id) => activeFilters[id]);
    if (activeRaterFilters.length > 0) {
      params.rater_names = activeRaterFilters.join(',');
    }

    if (searchId.trim()) {
      params.response_id = searchId.trim();
    }

    try {
      const result = await readResponseQuality({ params, signal, surveyId });
      setResponses(result.results);
      setTotalCount(result.count);
    } catch (err) {
      if (!signal.aborted) {
        setError(err);
      }
    }

    if (!signal.aborted) {
      setIsLoading(false);
    }
  };

  const handleFilterChange = useCallback((newFilters) => {
    setActiveFilters(newFilters);
    setCurrentPage(1);
  }, []);

  const handleOverride = useCallback(
    async ({ quarantine, reason, responseId, responseIds }) => {
      const resolvedResponseIds = responseIds || [responseId].filter(Boolean);
      if (resolvedResponseIds.length === 0) return;

      try {
        await quarantineResponses({
          quarantine,
          reason,
          responseIds: resolvedResponseIds,
          surveyId,
        });
        const responseLabel = resolvedResponseIds.length === 1 ? 'Response' : 'Responses';
        toast.success(
          quarantine
            ? `${responseLabel} removed from Results.`
            : `${responseLabel} reinstated in Results.`
        );
        const controller = new AbortController();
        await Promise.all([fetchCounts(controller.signal), fetchResponses(controller.signal)]);
        return true;
      } catch {
        toast.error('Failed to update response. Please try again.');
        return false;
      }
    },
    [surveyId, activeFilters, currentPage, searchId, sortBy]
  );

  const handleSearchChange = useCallback((value) => {
    setSearchId(value);
    setCurrentPage(1);
  }, []);

  const pageCount = useMemo(() => Math.ceil(totalCount / PAGE_SIZE), [totalCount]);

  if (error) return <Error status={error?.response?.status} />;

  return (
    <div className={styles['response-quality']}>
      <OverviewMetrics
        excludedCount={counts.excluded}
        includedCount={counts.included}
        isLoading={isCountsLoading}
      />
      <ResponseTable
        activeFilters={activeFilters}
        currentPage={currentPage}
        isLoading={isLoading}
        onFilterChange={handleFilterChange}
        onOverride={handleOverride}
        onPageChange={setCurrentPage}
        onSearchChange={handleSearchChange}
        onSortChange={setSortBy}
        pageCount={pageCount}
        responses={responses}
        searchId={searchId}
        surveyQuestions={surveyQuestions}
      />
    </div>
  );
};

ResponseQuality.propTypes = {
  publicToken: PropTypes.string,
  surveyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  surveyQuestions: PropTypes.arrayOf(PropTypes.object),
};

export default ResponseQuality;
